import { useCallback, useEffect, useRef, useState } from 'react';

const FOCUS_VISIBLE_KEYS = ['Tab', 'Escape'];

export function useFocusVisibleRef<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [isFocusVisible, setIsFocusVisible] = useState(false);
  const hadKeyboardEvent = useRef(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        !e.altKey &&
        !e.ctrlKey &&
        !e.metaKey &&
        FOCUS_VISIBLE_KEYS.includes(e.key)
      ) {
        hadKeyboardEvent.current = true;
      }
    };

    const handleMouseDown = () => {
      hadKeyboardEvent.current = false;
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('mousedown', handleMouseDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('mousedown', handleMouseDown, true);
    };
  }, []);

  useEffect(() => {
    const node = ref.current;

    if (!node) {
      return;
    };

    const handleFocus = () => {
      if (hadKeyboardEvent.current) {
        setIsFocusVisible(true);
      }
    };

    const handleBlur = () => {
      setIsFocusVisible(false);
    };

    node.addEventListener('focus', handleFocus, true);
    node.addEventListener('blur', handleBlur, true);

    return () => {
      node.removeEventListener('focus', handleFocus, true);
      node.removeEventListener('blur', handleBlur, true);
    };
  }, []);

  const handleSetRef = useCallback((node: T | null) => {
    ref.current = node;
  }, []);

  return { ref: handleSetRef, isFocusVisible };
}
